import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Typography,
  Card,
  Row,
  Col,
  Space,
  Divider,
  Statistic,
  Tag,
  Tooltip,
  Select,
  message,
  Checkbox,
  Empty,
  Spin,
  Modal,
} from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined, ToolOutlined, SearchOutlined, StopOutlined } from '@ant-design/icons';
import { catMatches } from '../../utils/searchNorm';
import { highlightText } from '../../components/Highlight';
import { getApprovableLines, createPO, costCenterLabel, requestMRLineCancel, decideMRLineCancel } from '../../api';
import { useAuth } from '../../context/AuthContext';
import RefineLineModal from '../../components/RefineLineModal';
import { ItemCell, VendorCell } from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';

const { Title, Text } = Typography;
const { TextArea } = Input;

/** A line with no catalog code: a stated gap where the code chip would sit. */
const freeTextMark = (
  <span
    style={{
      display: 'inline-block',
      padding: '0 6px',
      fontSize: 11,
      lineHeight: '18px',
      color: '#8a94a6',
      border: '1px dashed #c9cede',
      borderRadius: 4,
      whiteSpace: 'nowrap',
    }}
  >
    free text
  </span>
);

/** Code · EN · AR in one cell; free-text lines say so instead of showing an empty code. */
const renderItem = (line: {
  partNumber?: string;
  itemDescription: string;
  catalogItem?: { id?: number; partNumber: string; description?: string; descriptionAr?: string | null } | null;
}, highlight?: string, pictureUrl?: string | null, onPictureError?: () => void) => {
  const code = line.catalogItem?.partNumber || line.partNumber;
  const ciId = line.catalogItem?.id ?? null;
  const cell = (
    <ItemCell
      partNumber={code}
      description={line.catalogItem?.description || line.itemDescription}
      descriptionAr={line.catalogItem?.descriptionAr}
      linkTo={line.catalogItem?.id ? `/catalog/${line.catalogItem.id}` : undefined}
      highlight={highlight}
      showThumb
      pictureUrl={pictureUrl}
      thumbItemId={ciId}
      onThumbError={onPictureError}
    />
  );
  if (code) return cell;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      {freeTextMark}
      <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
    </div>
  );
};

interface ApprovableLine {
  catalogItem?: { id: number; partNumber: string; description?: string; descriptionAr?: string | null; unit?: { id: number; name: string } | null } | null;
  id: number;
  lineNo: number;
  partNumber?: string;
  itemDescription: string;
  qty: number | string;
  unitPrice: number | string;
  lineTotal: number | string;
  mrHeader: {
    id: number;
    mrId: string;
    department: { name: string; code: string };
  };
  vendor: { id: number; name: string } | null;
  category: { name: string } | null;
  unit: { name: string } | null;
  currency: { code: string } | null;
  costCenter?: any;
  /** Sent back by a PO line cancellation (2026-09-14): qty is what is left to order. */
  reorderFromPoId?: string | null;
  reorderReason?: string | null;
  originalQty?: number | string;
}

interface VendorGroup {
  vendorId: number | null;
  vendorName: string;
  lines: ApprovableLine[];
  total: number;
}

const POCreate: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [lines, setLines] = useState<ApprovableLine[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState('');
  const [reviewed, setReviewed] = useState(false);
  // Search / narrowing (2026-09-08): one box over suppliers and lines, plus a supplier filter.
  const [query, setQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState<string | null>(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const searchRef = useRef<any>(null);
  const { isProcurement, isAdmin, isApprover } = useAuth();
  const canRefine = isProcurement || isAdmin;
  // MR line cancellation from Create PO (2026-09-15): CEO/admin cancel directly, procurement asks the CEO.
  const canCancelDirect = isApprover || isAdmin;
  const [cancelBusy, setCancelBusy] = useState(false);
  // Bulk: cancel (or request cancellation of) every ticked line with one reason.
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  // Item pictures (2026-09-22) for the approved lines waiting to be ordered.
  const lineItemIds = useMemo(() => lines.map((l) => l.catalogItem?.id ?? null), [lines]);
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);
  const pictureOf = (l: ApprovableLine) => (l.catalogItem?.id ? linePictures[l.catalogItem.id]?.url ?? null : null);
  const onPictureError = (l: ApprovableLine) =>
    l.catalogItem?.id ? () => reloadLinePicture(l.catalogItem!.id) : undefined;
  const submitBulkCancel = async () => {
    const reason = bulkReason.trim();
    if (!reason) { message.warning('A reason is required'); return; }
    const targets = lines.filter((l) => selectedIds.has(l.id));
    setCancelBusy(true);
    let done = 0;
    const failed: string[] = [];
    try {
      for (const l of targets) {
        const mrId = l.mrHeader?.mrId;
        try {
          if (canCancelDirect) await decideMRLineCancel(mrId, l.id, { decision: 'approve', reason });
          else await requestMRLineCancel(mrId, l.id, reason);
          done += 1;
        } catch (err: any) {
          failed.push(`${mrId} line ${l.lineNo}: ${err.response?.data?.error || 'failed'}`);
        }
      }
      if (done > 0) {
        message.success(canCancelDirect
          ? `${done} line${done === 1 ? '' : 's'} cancelled`
          : `${done} line${done === 1 ? '' : 's'} sent to the CEO for approval`);
      }
      if (failed.length > 0) message.error(failed.join(' · '), 8);
      setSelectedIds(new Set());
      setBulkCancelOpen(false);
      setBulkReason('');
      fetchLines();
    } finally {
      setCancelBusy(false);
    }
  };
  const [refineLine, setRefineLine] = useState<ApprovableLine | null>(null);
  const [refineOpen, setRefineOpen] = useState(false);

  const fetchLines = async () => {
      setLoading(true);
      try {
        const data = await getApprovableLines();
        setLines(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        message.error('Failed to load approved lines');
      } finally {
        setLoading(false);
      }
    };
  useEffect(() => { fetchLines(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Group lines by vendor
  const vendorGroups: VendorGroup[] = React.useMemo(() => {
    const groups: Map<string, VendorGroup> = new Map();
    lines.forEach((line) => {
      const key = line.vendor?.id ? String(line.vendor.id) : 'unassigned';
      if (!groups.has(key)) {
        groups.set(key, {
          vendorId: line.vendor?.id || null,
          vendorName: line.vendor?.name || 'Unassigned Vendor',
          lines: [],
          total: 0,
        });
      }
      const group = groups.get(key)!;
      group.lines.push(line);
      group.total += Number(line.lineTotal) || (Number(line.qty) * Number(line.unitPrice));
    });
    return Array.from(groups.values()).sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  }, [lines]);

  // Which fields the search box looks at, per line. Supplier name/code match every line of the group.
  const lineMatches = (line: ApprovableLine, q: string) =>
    catMatches(
      q,
      line.mrHeader?.mrId,
      line.catalogItem?.partNumber || line.partNumber,
      line.catalogItem?.description || line.itemDescription,
      line.catalogItem?.descriptionAr,
      line.costCenter ? costCenterLabel(line.costCenter) : null,
      line.vendor?.name,
      (line.vendor as any)?.appCode,
    );

  const trimmedQuery = query.trim();
  const filteredGroups: VendorGroup[] = React.useMemo(() => {
    const out: VendorGroup[] = [];
    for (const group of vendorGroups) {
      const groupKey = group.vendorId ? String(group.vendorId) : 'unassigned';
      // "Show selected only" shows every selected line, ignoring the search and supplier filter.
      if (!showSelectedOnly && vendorFilter && vendorFilter !== groupKey) continue;
      const visible = group.lines.filter((l) =>
        showSelectedOnly ? selectedIds.has(l.id) : !trimmedQuery || lineMatches(l, trimmedQuery),
      );
      if (visible.length === 0) continue;
      out.push({
        ...group,
        lines: visible,
        total: visible.reduce((sum, l) => sum + (Number(l.lineTotal) || Number(l.qty) * Number(l.unitPrice)), 0),
      });
    }
    return out;
  }, [vendorGroups, vendorFilter, trimmedQuery, showSelectedOnly, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleLineCount = filteredGroups.reduce((n, g) => n + g.lines.length, 0);
  const visibleIds = new Set(filteredGroups.flatMap((g) => g.lines.map((l) => l.id)));
  const hiddenSelectedCount = Array.from(selectedIds).filter((id) => !visibleIds.has(id)).length;
  const isNarrowed = !!trimmedQuery || !!vendorFilter || showSelectedOnly;

  // "/" focuses the search, Escape clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = searchRef.current?.input as HTMLInputElement | undefined;
      const target = e.target as HTMLElement | null;
      const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (e.key === '/' && !typing) { e.preventDefault(); el?.focus(); }
      if (e.key === 'Escape' && target === el) { setQuery(''); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selectedTotal = lines
    .filter((l) => selectedIds.has(l.id))
    .reduce((sum, l) => sum + (Number(l.lineTotal) || Number(l.qty) * Number(l.unitPrice)), 0);

  const toggleLine = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleVendorGroup = (vendorLines: ApprovableLine[], checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      vendorLines.forEach((l) => {
        if (checked) {
          next.add(l.id);
        } else {
          next.delete(l.id);
        }
      });
      return next;
    });
  };

  const handleCreate = async () => {
    if (selectedIds.size === 0) {
      message.warning('Please select lines to create a PO');
      return;
    }
    if (!reviewed) {
      message.warning('Please confirm the review checkbox before creating the PO');
      return;
    }

    setCreating(true);
    try {
      const result = await createPO(Array.from(selectedIds), notes || undefined, true);
      // result is an array of POs (one per vendor); navigate to the first one
      const firstPO = Array.isArray(result) ? result[0] : result;
      const poDbId = firstPO.id;
      message.success('Purchase Order created successfully');
      navigate(`/pos/${poDbId}`);
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create PO');
    } finally {
      setCreating(false);
    }
  };

  const lineColumns = [
    {
      title: '',
      width: 50,
      render: (_: any, record: ApprovableLine) =>
        record.vendor?.id ? (
          <Checkbox
            checked={selectedIds.has(record.id)}
            onChange={(e) => toggleLine(record.id, e.target.checked)}
          />
        ) : (
          <Tooltip title="No supplier on this line. Refine it and choose a supplier before it can go on a PO.">
            <Checkbox disabled />
          </Tooltip>
        ),
    },
    {
      title: 'MR',
      key: 'mrId',
      width: 150,
      render: (_: any, record: ApprovableLine) => {
        const status = (record.mrHeader as any)?.status as string | undefined;
        const pendingSiblings = status === 'Partially Approved' || status === 'Submitted';
        return (
          <Space direction="vertical" size={0}>
            <span>{highlightText(record.mrHeader?.mrId || '-', trimmedQuery)}</span>
            {status && (
              <Tooltip title={pendingSiblings ? 'Other lines on this MR are still waiting for approval' : undefined}>
                <Tag color={pendingSiblings ? 'orange' : 'default'} style={{ fontSize: 11, lineHeight: '16px', margin: 0 }}>
                  {status}
                </Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Line #',
      dataIndex: 'lineNo',
      width: 70,
      align: 'center' as const,
    },
    {
      // Flexible on purpose: the code must never be clipped or wrapped mid-code.
      title: 'Item',
      key: 'item',
      render: (_: any, record: ApprovableLine) => (
        <div>
          {renderItem(record, trimmedQuery, pictureOf(record), onPictureError(record))}
          {record.reorderFromPoId && (
            <div style={{ fontSize: 11.5, color: '#9a5b00', marginTop: 2 }}>
              back from {record.reorderFromPoId}{record.reorderReason ? ` · ${record.reorderReason}` : ''} · {Number(record.qty)} of {Number(record.originalQty)} to order again
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Vendor',
      key: 'vendor',
      width: 150,
      render: (_: any, record: ApprovableLine) => (
        <VendorCell
          name={record.vendor?.name}
          appCode={(record.vendor as any)?.appCode}
          missingLabel="unassigned"
          highlight={trimmedQuery}
        />
      ),
    },
    {
      title: 'Cost Center',
      key: 'costCenter',
      width: 180,
      ellipsis: true,
      render: (_: any, record: ApprovableLine) => record.costCenter ? highlightText(costCenterLabel(record.costCenter), trimmedQuery) : <Tag>None</Tag>,
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: 70,
      align: 'right' as const,
      render: (val: any) => Number(val),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unitPrice',
      width: 110,
      align: 'right' as const,
      render: (val: any) => Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 }),
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 80,
      render: (_: any, record: ApprovableLine) => record.currency?.code || '-',
    },
    {
      title: 'Total',
      key: 'total',
      width: 110,
      align: 'right' as const,
      render: (_: any, record: ApprovableLine) =>
        (Number(record.lineTotal) || (Number(record.qty) * Number(record.unitPrice))).toLocaleString('en-US', {
          minimumFractionDigits: 2,
        }),
    },
    ...(canRefine ? [{
      title: '',
      key: 'refine',
      width: 110,
      render: (_: any, record: ApprovableLine) => (
        <Button size="small" icon={<ToolOutlined />} onClick={() => { setRefineLine(record); setRefineOpen(true); }}>
          {record.catalogItem ? 'Refine' : 'Assign code'}
        </Button>
      ),
    }] : []),
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/pos'))}>
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              Create Purchase Order
            </Title>
          </Space>
        </Col>
      </Row>

      {lines.length === 0 ? (
        <Card>
          <Empty description="No approved lines available for ordering" />
        </Card>
      ) : (
        <>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Select approved MR lines to create a Purchase Order. Lines should be from the same vendor.
          </Text>

          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 5,
              background: '#f5f6fa',
              padding: '4px 0 10px',
              marginBottom: 6,
            }}
          >
            <Row gutter={8} align="middle" wrap={false}>
              <Col flex="auto">
                <Input
                  ref={searchRef}
                  allowClear
                  size="large"
                  prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
                  suffix={!trimmedQuery ? <Text type="secondary" style={{ fontSize: 11, border: '1px solid #e3e6ec', borderRadius: 4, padding: '0 5px' }}>/</Text> : null}
                  placeholder="Search supplier, MR number, item code, description (English or Arabic), cost center…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </Col>
              <Col flex="230px">
                <Select
                  allowClear
                  size="large"
                  style={{ width: '100%' }}
                  placeholder="All suppliers"
                  value={vendorFilter ?? undefined}
                  onChange={(v) => setVendorFilter(v ?? null)}
                  showSearch
                  optionFilterProp="label"
                  options={vendorGroups.map((g) => ({
                    value: g.vendorId ? String(g.vendorId) : 'unassigned',
                    label: `${g.vendorName} (${g.lines.length})`,
                  }))}
                />
              </Col>
              <Col flex="none">
                <Text type="secondary" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                  <Text strong>{visibleLineCount}</Text> lines · <Text strong>{filteredGroups.length}</Text> suppliers
                  {isNarrowed && (
                    <>
                      {' '}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => { setQuery(''); setVendorFilter(null); setShowSelectedOnly(false); }}
                      >
                        show all
                      </Button>
                    </>
                  )}
                </Text>
              </Col>
              {(canRefine || canCancelDirect) && (
                <Col flex="none">
                  <Tooltip title={selectedIds.size === 0 ? 'Tick the lines to cancel first' : (canCancelDirect ? 'Cancel the ticked lines; they leave Create PO and stay on their MRs as history' : 'Ask the CEO to cancel the ticked lines')}>
                    <Button
                      size="large"
                      danger
                      icon={<StopOutlined />}
                      disabled={selectedIds.size === 0}
                      onClick={() => { setBulkReason(''); setBulkCancelOpen(true); }}
                    >
                      {canCancelDirect ? 'Cancel lines' : 'Request cancellation'}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                    </Button>
                  </Tooltip>
                </Col>
              )}
            </Row>
          </div>

          {filteredGroups.length === 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Empty
                description={
                  showSelectedOnly
                    ? 'No lines selected yet.'
                    : 'No approved lines match. Clear the search or pick another supplier.'
                }
              />
            </Card>
          )}

          {filteredGroups.map((group) => {
            const fullCount = vendorGroups.find((g) => g.vendorId === group.vendorId)?.lines.length ?? group.lines.length;
            const selectable = group.lines.filter((l) => !!l.vendor?.id);
            const allSelected = selectable.length > 0 && selectable.every((l) => selectedIds.has(l.id));
            const someSelected = selectable.some((l) => selectedIds.has(l.id));

            return (
              <Card
                key={group.vendorId || 'unassigned'}
                title={
                  <Space>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected && !allSelected}
                      onChange={(e) => toggleVendorGroup(selectable, e.target.checked)}
                      disabled={selectable.length === 0}
                    />
                    <Text strong>{highlightText(group.vendorName, trimmedQuery)}</Text>
                    <Tag>{group.lines.length === fullCount ? `${group.lines.length} lines` : `${group.lines.length} of ${fullCount} lines`}</Tag>
                    <Text type="secondary">
                      Total: {group.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </Text>
                  </Space>
                }
                style={{ marginBottom: 16 }}
                size="small"
              >
                <Table
                  columns={lineColumns}
                  dataSource={group.lines}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: 1496 }}
                />
              </Card>
            );
          })}

          {selectedIds.size > 0 && (
            <Card
              title="Review — Confirm qty, unit, and price for each selected line"
              style={{ marginTop: 16, borderColor: '#faad14' }}
              size="small"
            >
              <Table
                size="small"
                pagination={false}
                rowKey="id"
                dataSource={lines.filter((l) => selectedIds.has(l.id))}
                columns={[
                  {
                    title: 'MR',
                    key: 'mr',
                    width: 110,
                    render: (_: any, r: ApprovableLine) => r.mrHeader?.mrId || '-',
                  },
                  { title: 'Line #', dataIndex: 'lineNo', width: 70, align: 'center' as const },
                  {
                    title: 'Item',
                    key: 'item',
                    render: (_: any, r: ApprovableLine) => renderItem(r, undefined, pictureOf(r), onPictureError(r)),
                  },
                  {
                    title: 'Qty',
                    dataIndex: 'qty',
                    width: 80,
                    align: 'right' as const,
                    render: (v: any) => Number(v),
                  },
                  {
                    title: 'Unit',
                    key: 'unit',
                    width: 90,
                    render: (_: any, r: ApprovableLine) => r.unit?.name || '-',
                  },
                  {
                    title: 'Unit Price',
                    dataIndex: 'unitPrice',
                    width: 110,
                    align: 'right' as const,
                    render: (v: any) =>
                      Number(v).toLocaleString('en-US', { minimumFractionDigits: 2 }),
                  },
                  {
                    title: 'Line Total',
                    key: 'lineTotal',
                    width: 120,
                    align: 'right' as const,
                    render: (_: any, r: ApprovableLine) =>
                      (
                        Number(r.lineTotal) || Number(r.qty) * Number(r.unitPrice)
                      ).toLocaleString('en-US', { minimumFractionDigits: 2 }),
                  },
                ]}
              />
              <Divider style={{ margin: '12px 0' }} />
              <Checkbox
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
              >
                <Text strong>
                  I have reviewed each line total and confirm the quantities, units, and prices
                  are correct
                </Text>
              </Checkbox>
            </Card>
          )}

          <Card style={{ marginTop: 16 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <TextArea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="PO notes (optional)"
                  style={{ maxWidth: 500 }}
                />
              </Col>
              <Col>
                <Space direction="vertical" align="end">
                  <Statistic
                    title="Selected Lines"
                    value={selectedIds.size}
                    suffix={`/ ${lines.length}`}
                  />
                  {hiddenSelectedCount > 0 && (
                    <Tag color="orange" style={{ margin: 0 }}>
                      {hiddenSelectedCount} selected line{hiddenSelectedCount > 1 ? 's' : ''} hidden by the search
                    </Tag>
                  )}
                  {selectedIds.size > 0 && (
                    <Button size="small" onClick={() => setShowSelectedOnly((v) => !v)}>
                      {showSelectedOnly ? 'Show all lines' : 'Show selected only'}
                    </Button>
                  )}
                  <Statistic
                    title="Selected Total"
                    value={selectedTotal}
                    precision={2}
                    prefix="$"
                  />
                  <Button
                    type="primary"
                    size="large"
                    icon={<ShoppingCartOutlined />}
                    loading={creating}
                    onClick={handleCreate}
                    disabled={selectedIds.size === 0 || !reviewed}
                  >
                    Create Purchase Order
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>
        </>
      )}
      <Modal
        title={`${canCancelDirect ? 'Cancel' : 'Request cancellation of'} ${selectedIds.size} selected line${selectedIds.size === 1 ? '' : 's'}`}
        open={bulkCancelOpen}
        onOk={submitBulkCancel}
        onCancel={() => setBulkCancelOpen(false)}
        confirmLoading={cancelBusy}
        okText={canCancelDirect ? 'Cancel lines' : 'Send to CEO'}
        okButtonProps={{ danger: true }}
        cancelText="Keep them"
        width={640}
        destroyOnClose
      >
        <Text type="secondary">
          {canCancelDirect
            ? 'They leave Create PO and stay on their MRs as history; nothing is deleted.'
            : 'Goes to the CEO for approval. The lines are held out of Create PO until decided.'}
        </Text>
        <div style={{ maxHeight: 260, overflowY: 'auto', margin: '12px 0', border: '1px solid #e3e6ec', borderRadius: 6 }}>
          {lines.filter((l) => selectedIds.has(l.id)).map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 10px', borderBottom: '1px solid #f0f1f4', fontSize: 13 }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ color: '#8a94a6', marginRight: 8 }}>{l.mrHeader?.mrId} · {l.lineNo}</span>
                {l.catalogItem?.partNumber || l.partNumber ? <code style={{ marginRight: 6 }}>{l.catalogItem?.partNumber || l.partNumber}</code> : null}
                {l.catalogItem?.description || l.itemDescription}
              </span>
              <span style={{ whiteSpace: 'nowrap', color: '#4b5563' }}>{Number(l.qty)} {l.unit?.name || ''}</span>
            </div>
          ))}
        </div>
        <div><Text strong>Reason</Text> <Text type="danger">*</Text> <Text type="secondary" style={{ fontSize: 12 }}>(same reason on every line)</Text></div>
        <TextArea
          rows={3}
          maxLength={500}
          value={bulkReason}
          onChange={(e) => setBulkReason(e.target.value)}
          placeholder="Why these lines are no longer needed"
          style={{ marginTop: 4 }}
        />
      </Modal>
      <RefineLineModal
        line={refineLine as any}
        open={refineOpen}
        onClose={() => { setRefineOpen(false); setRefineLine(null); }}
        onSaved={fetchLines}
        departmentId={(refineLine as any)?.mrHeader?.department?.id ?? (refineLine as any)?.mrHeader?.departmentId ?? null}
      />
    </div>
  );
};

export default POCreate;
